import isNil from 'lodash/isNil';
import {useState} from 'react';
import {t} from '../../../common/getTranslations';
import {getFormattedNumberWithSuffix} from '../../../common/shared/utils/getFormattedNumberWithSuffix';
import {twMerge} from '../../../common/twMerge';
import KakaoEmoticon from '../../../domain/comment/presentational/KakaoEmoticon';
import {getCommentReplyIconType} from '../../../domain/comment/util/getCommentReplyIconType';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {CommentBottomAreaProps} from './CommentBottomAreaProps.interface';

export function CommentBottomArea({
  onlyText,
  isMyComment,
  isSpoiler,
  emoticon,
  comment,
  seriesText,
  likeCount,
  replyCount,
  isLikeActive,
  isReplyActive,
  hasMyReplyComment,
  shouldShowInfringeRight,
  shouldHideLikeButton,
  shouldHideReplyButton,
  onClickMore,
  onClickLike,
  onClickReply,
  onClickSpoilerVisible,
  className,
}: CommentBottomAreaProps) {
  const [isSpoilerVisible, setIsSpoilerVisible] = useState(false);

  if (onlyText && onlyText !== '') {
    return (
      <div className={twMerge('flex justify-center', className)}>
        <Text className="font-small2 text-theme-solid-40">{onlyText}</Text>
      </div>
    );
  }
  return (
    <div className={twMerge('relative flex flex-col', className)}>
      {isSpoiler && !isMyComment && !isSpoilerVisible && (
        <div className="absolute left-0 top-0 z-1 inline-flex">
          <Text
            className="font-small1 cursor-pointer text-static-point-red"
            onClick={e => {
              setIsSpoilerVisible(true);
              onClickSpoilerVisible && onClickSpoilerVisible(e);
            }}
          >
            {t('comment_spoiler_view')}
          </Text>
        </div>
      )}
      <div className="flex flex-col space-y-8pxr">
        {isSpoiler && isMyComment && (
          <div className="inline-flex">
            <Text className="font-small1 text-static-point-red">
              {t('comment_spoiler')}
            </Text>
          </div>
        )}
        {emoticon && (
          <div
            className={twMerge(
              isSpoiler && !isMyComment && !isSpoilerVisible && 'opacity-5'
            )}
          >
            <KakaoEmoticon
              itemId={emoticon.itemId}
              resourceId={emoticon.resourceId}
              itemSubType={emoticon.itemSubType}
            />
          </div>
        )}
        {comment && (
          <Text
            className={twMerge(
              'font-medium2 whitespace-pre-wrap text-theme-solid-100 break-word-anywhere',
              isSpoiler && !isMyComment && !isSpoilerVisible && 'opacity-5'
            )}
          >
            {comment}
          </Text>
        )}
        {seriesText && (
          <Text className="font-small2 break-all text-theme-solid-60 line-clamp-1">
            {seriesText}
          </Text>
        )}
      </div>
      {/* 권리침해신고 영역 */}
      {!!shouldShowInfringeRight && (
        <div className="mt-18pxr flex h-56pxr flex-col rounded-8pxr bg-theme-transparent-10 p-12pxr">
          <div className="flex items-center">
            <Icon
              type={IconType.MoreNoticeSolidRedStatic}
              className="mr-4pxr"
            />
            <Text className="font-x-small1 text-theme-solid-60">
              {t('comment_copyright_issue')}
            </Text>
          </div>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://${window.location.host}/policy?type=right`}
            className="ml-16pxr flex cursor-pointer items-center"
          >
            <Text className="font-x-small1 text-theme-solid-100">
              {t('comment_copyright_issue_details')}
            </Text>
            <Icon type={IconType.ButtonArrowRight} />
          </a>
        </div>
      )}
      {/* 정보 영역 */}
      <div
        className={twMerge(
          // 디자인에서 20px로 변경요청하여 20px로 변경 합니다. (KW-8145 댓글 참고)
          'mt-20pxr flex',
          !!shouldShowInfringeRight && 'mt-10pxr'
        )}
      >
        <div className="flex flex-1">
          {!isNil(likeCount) && !shouldHideLikeButton && (
            <div
              className="mr-8pxr flex h-28pxr cursor-pointer items-center space-x-3pxr rounded-8pxr bg-theme-transparent-10 px-12pxr"
              onClick={onClickLike}
            >
              <Icon
                type={
                  isLikeActive ? IconType.ListHeartOn : IconType.ListHeartOff
                }
                disableActiveEffect
              />
              <Text className="font-small2 text-theme-solid-60">
                {likeCount === 0
                  ? t('comment_like')
                  : getFormattedNumberWithSuffix(likeCount)}
              </Text>
            </div>
          )}
          {replyCount !== undefined && !shouldHideReplyButton && (
            <div
              className="flex h-28pxr cursor-pointer items-center space-x-3pxr rounded-8pxr bg-theme-transparent-10 px-12pxr"
              onClick={onClickReply}
            >
              <Icon
                type={getCommentReplyIconType(isReplyActive, hasMyReplyComment)}
                disableActiveEffect
              />
              <Text className="font-small2 text-theme-solid-60">
                {!replyCount || replyCount === 0
                  ? t('comment_reply')
                  : getFormattedNumberWithSuffix(replyCount)}
              </Text>
            </div>
          )}
        </div>
        <div
          className="flex h-28pxr w-28pxr cursor-pointer items-center justify-center rounded-8pxr bg-theme-transparent-10"
          {...putTiaraObjectProps({click: {layer1: '더보기'}})}
          onClick={onClickMore}
        >
          <Icon type={isMyComment ? IconType.ListClose : IconType.ListMore} />
        </div>
      </div>
    </div>
  );
}
